import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IndianRupee,
  Users,
  Clock,
  TrendingUp,
  Download,
  Plus,
  FileText,
  Edit2,
  CreditCard,
  SlidersHorizontal,
  Gift,
  Trash2,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../../components/Tables/APITable";
import Button from "../../../components/Button/Button";
import Modal from "../../../components/Modal/Modal";
import Input from "../../../components/Input/Input";
import DateRangePicker from "../../../components/Calender/DateRangePicker";
import Pagination from "../../../components/Pagination/Pagination";
import Notification from "../../../components/Notification/Notification";
import {
  StatsSkeleton,
  TableSkeleton,
} from "../../../components/Skeleton/StatsSkeleton";

/* ===================== API SERVICE ===================== */
import { apiGet, apiPost, apiDelete } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./BillingManagement.module.scss";

const BillingManagement = () => {
  const navigate = useNavigate();

  // Granted actions on the "billing" resource (from /modules/sidebar)
  const canDo = (action) => can("billing", action);

  /* ===================== UI STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [orgList, setOrgList] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [companyDetail, setCompanyDetail] = useState(null);
  const [credits, setCredits] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTxnSubmitting, setIsTxnSubmitting] = useState(false);

  const [errors, setErrors] = useState({});

  /* ===================== GIFT CREDITS (INTERNAL ONLY) ===================== */
  const isInternalUser = localStorage.getItem("userType") === "Internal";
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftOrgId, setGiftOrgId] = useState("");
  const [giftCredits, setGiftCredits] = useState("");
  const [giftReason, setGiftReason] = useState("");
  const [giftErrors, setGiftErrors] = useState({});
  const [isGiftSubmitting, setIsGiftSubmitting] = useState(false);

  /* ===================== NOTIFICATION ===================== */
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "error") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* ===================== STATS STATE ===================== */
  const [stats, setStats] = useState({
    revenue: 0,
    growth: 0,
    pendingCount: 0,
    pendingAmount: 0,
    activeClients: 0,
  });
  const [showTxnModal, setShowTxnModal] = useState(false);
  const [selectedBillingId, setSelectedBillingId] = useState(null);
  const [txnIdInput, setTxnIdInput] = useState("");

  const openTxnModal = (row) => {
    setSelectedBillingId(row.id);
    setTxnIdInput("");
    setShowTxnModal(true);
  };

  /* ===================== DELETE INVOICE ===================== */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const openDeleteModal = (row) => {
    setDeleteTarget(row);
    setDeleteReason("");
    setShowDeleteModal(true);
  };

  /* ===================== TABLE STATE ===================== */
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingstate, setLoadingstate] = useState(false);

  /* ===================== PAGINATION ===================== */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  /* ===================== DATE FILTER ===================== */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  /* ===================== TABLE COLUMNS ===================== */
  const columns = [
    { label: "Invoice ID", key: "invoiceId" },
    { label: "Customer", key: "customer" },
    { label: "Email", key: "email" },
    { label: "Plan", key: "plan" },
    { label: "Credits", key: "credits" },
    { label: "Amount", key: "amount" },
    { label: "Date", key: "date" },
    { label: "Status", key: "status" },
    { label: "Actions", key: "action" },
  ];

  const validateForm = () => {
    let newErrors = {};

    if (!selectedOrgId) newErrors.customer = "This field is required";
    if (!credits || Number(credits) <= 0)
      newErrors.credits = "This field is required";
    if (!paymentStatus) newErrors.status = "This field is required";
    if (paymentStatus === "Paid" && !transactionId.trim())
      newErrors.transactionId = "This field is required";
    if (!companyDetail?.address)
      newErrors.customer =
        "Please fill the address and state first of this organization";
    if (!companyDetail?.state)
      newErrors.customer =
        "Please fill the address and state first of this organization";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ===================== DASHBOARD STATS API ===================== */
  const fetchBillingDashboard = async () => {
    setLoadingstate(true);
    try {
      const res = await apiGet("billing", "/pay-dashboard");
      if (res?.success) {
        setStats((prev) => ({
          ...prev,
          revenue: res.billingData.current_revenue,
          growth: res.billingData.growth_rate,
          pendingCount: res.billingData.pending_invoices_count,
          pendingAmount: res.billingData.pending_amount,
        }));
      }
    } catch (err) {
      console.error("Billing dashboard error", err);
    } finally {
      setLoadingstate(false);
    }
  };

  const fetchActiveClients = async () => {
    try {
      const res = await apiGet("catalog", "/active-client");
      if (res?.status === "success" && res.data?.length) {
        setStats((prev) => ({
          ...prev,
          activeClients: res.data[0].active_client_count || 0,
        }));
      }
    } catch (error) {
      console.error("Active client fetch error", error);
    }
  };

  /* ===================== BILLING LIST API ===================== */
  const fetchBillingList = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page,
        limit,
        ...(activeTab !== "All" && { status: activeTab }),
        ...(fromDate && toDate && { from_date: fromDate, to_date: toDate }),
      }).toString();

      const res = await apiGet("billing", `/pay?${query}`);

      if (res?.success) {
        const formatted = res.data.map((item) => ({
          id: item.id,
          invoiceId: item.invoice_no,
          customer: item.company_detail?.companyName || "-",
          email: item.company_detail?.email || "-",
          plan: item.plan_name,
          credits: item.add_credits,
          amount: `₹${item.total_amount}`,
          date: item.invoice_date.split("T")[0],
          status: item.status,
          active: item.status === "Paid",
        }));

        setTableData(formatted);
        setPagination({
          total: res.pagination.total || formatted.length,
          totalPages: res.pagination.totalPages || 1,
        });
      }
    } catch (err) {
      console.error("Billing list error", err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, activeTab, fromDate, toDate]);

  const fetchOrganizations = async () => {
    const res = await apiGet("user", "/organization/dropdown");
    if (res?.status === "success") {
      setOrgList(
        res.data.map((item) => ({
          label: item.companyName,
          value: item.id,
        })),
      );
    }
  };

  const handleCustomerSelect = async (orgId) => {
    setSelectedOrgId(orgId);
    if (!orgId) {
      setCompanyDetail(null);
      return;
    }

    const res = await apiPost("user", "/company-detail", { id: orgId });
    if (res?.status === "success") {
      setCompanyDetail(res.data);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    const payload = {
      orgId: selectedOrgId,
      addCredits: Number(credits),
      status: paymentStatus,
      planName: "Enterprise Pack",
      paymentMethod: "Custom",
      transactionId: paymentStatus === "Paid" ? transactionId : null,
      companyDetail: {
        name: companyDetail.name,
        email: companyDetail.email,
        mobile: companyDetail.mobile,
        companyName: companyDetail.companyName,
        address: companyDetail.address,
        state: companyDetail.state,
        gstNo: companyDetail.gstNo,
      },
    };

    try {
      const res = await apiPost("billing", "/pay-insert", payload);
      if (res?.status === "success") {
        showNotification(res.message, "success");
        setShowModal(false);
        setSelectedOrgId("");
        setCompanyDetail(null);
        setCredits("");
        setPaymentStatus("");
        setTransactionId("");
        fetchBillingDashboard();
        setPage(1);
        fetchBillingList();
      } else {
        showNotification(res?.message || "Something went wrong", "error");
      }
    } catch (error) {
      showNotification(getErrorMessage(error), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateGiftForm = () => {
    const newErrors = {};
    if (!giftOrgId) newErrors.customer = "This field is required";
    if (!giftCredits || Number(giftCredits) <= 0)
      newErrors.credits = "This field is required";
    if (!giftReason.trim()) newErrors.reason = "This field is required";

    setGiftErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetGiftForm = () => {
    setGiftOrgId("");
    setGiftCredits("");
    setGiftReason("");
    setGiftErrors({});
  };

  const handleGiftSubmit = async () => {
    if (!validateGiftForm()) return;

    setIsGiftSubmitting(true);
    const payload = {
      orgId: giftOrgId,
      addCredits: Number(giftCredits),
      reason: giftReason.trim(),
    };

    try {
      const res = await apiPost("billing", "/gift-credits", payload);
      if (res?.status === "success") {
        showNotification(
          res.message || "Gift credits added successfully",
          "success",
        );
        setShowGiftModal(false);
        resetGiftForm();
        fetchBillingDashboard();
        setPage(1);
        fetchBillingList();
      } else {
        showNotification(res?.message || "Something went wrong", "error");
      }
    } catch (error) {
      showNotification(getErrorMessage(error), "error");
    } finally {
      setIsGiftSubmitting(false);
    }
  };

  const handleBillingUpdate = async () => {
    if (!txnIdInput.trim()) {
      showNotification("Transaction ID is required", "error");
      return;
    }

    setIsTxnSubmitting(true);
    try {
      const res = await apiPost("billing", "/pay-update", {
        id: selectedBillingId,
        transactionId: txnIdInput,
      });

      if (res?.status === "success") {
        showNotification("Payment marked as Paid", "success");
        setShowTxnModal(false);
        fetchBillingDashboard();
        fetchBillingList();
      } else {
        showNotification(res?.message || "Update failed", "error");
      }
    } catch (error) {
      showNotification(getErrorMessage(error), "error");
    } finally {
      setIsTxnSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    // Reason is optional — only include it when the user typed something.
    const payload = deleteReason.trim() ? { reason: deleteReason.trim() } : {};

    try {
      const res = await apiDelete(
        "billing",
        `/pay/${deleteTarget.id}`,
        payload,
      );

      if (res?.status === "success") {
        showNotification(
          res.message || "Invoice deleted successfully",
          "success",
        );
        setShowDeleteModal(false);
        setDeleteTarget(null);
        setDeleteReason("");

        // If the last row on a page was removed, step back a page.
        if (tableData.length === 1 && page > 1) {
          setPage((prev) => prev - 1);
        } else {
          fetchBillingList();
        }
        fetchBillingDashboard();
      } else {
        showNotification(res?.message || "Failed to delete invoice", "error");
      }
    } catch (error) {
      showNotification(getErrorMessage(error), "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    fetchBillingDashboard();
    fetchOrganizations();
    fetchActiveClients();
  }, []);

  useEffect(() => {
    fetchBillingList();
  }, [fetchBillingList]);

  /* ===================== CSV DOWNLOAD ===================== */
  const handleDownload = () => {
    const header = columns.map((c) => c.label).join(",") + "\n";
    const body = tableData
      .map((r) =>
        columns
          .filter((c) => c.key !== "action")
          .map((c) => r[c.key] ?? "")
          .join(","),
      )
      .join("\n");

    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "billing.csv";
    link.click();
  };

  /* ===================== JSX ===================== */
  return (
    <div className={styles["admin-billing-management__page"]}>
      {notification && (
        <div className={styles["admin-billing-management__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-billing-management__header"]}>
        <div className={styles["admin-billing-management__icon-wrapper"]}>
          <CreditCard size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-billing-management__title-group"]}>
          <h2 className={styles["admin-billing-management__title"]}>Billing</h2>
          <p className={styles["admin-billing-management__subtitle"]}>
            Track revenue, invoices, and subscriptions
          </p>
        </div>
        <div className={styles["admin-billing-management__header-actions"]}>
          {canDo("update-global-rate") && (
            <Button
              text="Update Global Settings"
              icon={SlidersHorizontal}
              onClick={() =>
                navigate("/admin/billing-management/global-settings")
              }
            />
          )}
        </div>
      </div>

      {/* STATS */}
      <div className={styles["admin-billing-management__stats-grid"]}>
        {loadingstate ? (
          [...Array(4)].map((_, i) => <StatsSkeleton key={i} />)
        ) : (
          <>
            <div className={styles["admin-billing-management__stat-card"]}>
              <div className={styles["admin-billing-management__stat-header"]}>
                <p className={styles["admin-billing-management__stat-label"]}>
                  Total Revenue
                </p>
                <div
                  className={`${styles["admin-billing-management__stat-icon"]} ${styles["admin-billing-management__stat-icon--green"]}`}
                >
                  <IndianRupee size={20} strokeWidth={2.5} />
                </div>
              </div>
              <h3 className={styles["admin-billing-management__stat-value"]}>
                ₹{stats.revenue}
              </h3>
              <span
                className={styles["admin-billing-management__stat-subtext"]}
              >
                {stats.growth}% from last period
              </span>
            </div>

            <div className={styles["admin-billing-management__stat-card"]}>
              <div className={styles["admin-billing-management__stat-header"]}>
                <p className={styles["admin-billing-management__stat-label"]}>
                  Active Subscriptions
                </p>
                <div
                  className={`${styles["admin-billing-management__stat-icon"]} ${styles["admin-billing-management__stat-icon--blue"]}`}
                >
                  <Users size={20} strokeWidth={2.5} />
                </div>
              </div>
              <h3 className={styles["admin-billing-management__stat-value"]}>
                {stats.activeClients}
              </h3>
              <span
                className={styles["admin-billing-management__stat-subtext"]}
              >
                Active users
              </span>
            </div>

            <div className={styles["admin-billing-management__stat-card"]}>
              <div className={styles["admin-billing-management__stat-header"]}>
                <p className={styles["admin-billing-management__stat-label"]}>
                  Pending Invoices
                </p>
                <div
                  className={`${styles["admin-billing-management__stat-icon"]} ${styles["admin-billing-management__stat-icon--orange"]}`}
                >
                  <Clock size={20} strokeWidth={2.5} />
                </div>
              </div>
              <h3 className={styles["admin-billing-management__stat-value"]}>
                {stats.pendingCount}
              </h3>
              <span
                className={styles["admin-billing-management__stat-subtext"]}
              >
                ₹{stats.pendingAmount} total
              </span>
            </div>

            <div className={styles["admin-billing-management__stat-card"]}>
              <div className={styles["admin-billing-management__stat-header"]}>
                <p className={styles["admin-billing-management__stat-label"]}>
                  Growth Rate
                </p>
                <div
                  className={`${styles["admin-billing-management__stat-icon"]} ${styles["admin-billing-management__stat-icon--purple"]}`}
                >
                  <TrendingUp size={20} strokeWidth={2.5} />
                </div>
              </div>
              <h3 className={styles["admin-billing-management__stat-value"]}>
                {stats.growth}%
              </h3>
              <span
                className={styles["admin-billing-management__stat-subtext"]}
              >
                vs previous period
              </span>
            </div>
          </>
        )}
      </div>

      {/* TABLE CARD */}
      <div className={styles["admin-billing-management__card"]}>
        <div className={styles["admin-billing-management__card-header"]}>
          <div>
            <h3
              className={styles["admin-billing-management__title"]}
              style={{ fontSize: "20px" }}
            >
              Recent Invoices
            </h3>
            <p className={styles["admin-billing-management__subtitle"]}>
              Latest billing transactions
            </p>
          </div>
          <div className={styles["admin-billing-management__header-actions"]}>
            {isInternalUser && canDo("add-gift-credits") && (
              <Button
                text="Add Gift Credits"
                icon={Gift}
                onClick={() => setShowGiftModal(true)}
              />
            )}
            {canDo("add-credits") && (
              <Button
                text="Add Credits"
                icon={Plus}
                onClick={() => setShowModal(true)}
              />
            )}
          </div>
        </div>

        <div className={styles["admin-billing-management__controls"]}>
          <div className={styles["admin-billing-management__showing-text"]}>
            Showing {tableData.length} of {pagination.total}
          </div>

          <div className={styles["admin-billing-management__filters"]}>
            <DateRangePicker
              key={activeTab}
              maxDate={new Date()}
              onChange={(type, value) => {
                if (type === "from") setFromDate(value);
                if (type === "to") setToDate(value);
              }}
            />
            {canDo("export") && (
              <button
                className={styles["admin-billing-management__download-btn"]}
                onClick={handleDownload}
              >
                <Download size={16} /> Download
              </button>
            )}
          </div>
        </div>

        <div className={styles["admin-billing-management__tabs"]}>
          {["All", "Paid", "Pending"].map((tab) => (
            <button
              key={tab}
              className={`${styles["admin-billing-management__tab"]} ${
                activeTab === tab
                  ? styles["admin-billing-management__tab--active"]
                  : ""
              }`}
              onClick={() => handleTabChange(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {loading ? (
          <TableSkeleton columns={8} rows={4} />
        ) : (
          <APITable
            columns={columns}
            data={tableData}
            renderCell={(row, key) => {
              if (key === "status") {
                return (
                  <span
                    className={`${styles["admin-billing-management__badge"]} ${
                      row.status === "Paid"
                        ? styles["admin-billing-management__badge--paid"]
                        : styles["admin-billing-management__badge--pending"]
                    }`}
                  >
                    {row.status}
                  </span>
                );
              }

              if (key === "action") {
                const showEdit =
                  row.status === "Pending" && canDo("update-invoice");

                if (
                  !canDo("view-invoice") &&
                  !showEdit &&
                  !canDo("delete-invoice")
                ) {
                  return "—";
                }

                return (
                  <div
                    className={styles["admin-billing-management__action-cell"]}
                  >
                    {canDo("view-invoice") && (
                      <Link
                        to={`/admin/billing-management/view/${row.id}`}
                        className={styles["admin-billing-management__icon-btn"]}
                        title="View Invoice"
                      >
                        <FileText size={18} strokeWidth={2.5} />
                      </Link>
                    )}

                    {showEdit && (
                      <div
                        className={styles["admin-billing-management__icon-btn"]}
                        onClick={() => openTxnModal(row)}
                        title="Mark as Paid"
                      >
                        <Edit2 size={18} strokeWidth={2.5} />
                      </div>
                    )}

                    {canDo("delete-invoice") && (
                      <div
                        className={`${styles["admin-billing-management__icon-btn"]} ${styles["admin-billing-management__icon-btn--danger"]}`}
                        onClick={() => openDeleteModal(row)}
                        title="Delete Invoice"
                      >
                        <Trash2 size={18} strokeWidth={2.5} />
                      </div>
                    )}
                  </div>
                );
              }
              return row[key];
            }}
          />
        )}

        <Pagination
          currentPage={page}
          totalPages={pagination.totalPages}
          rowsPerPage={limit}
          onPageChange={setPage}
          onRowsPerPageChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      </div>

      {/* ADD CREDITS MODAL */}
      {showModal && (
        <Modal title="Add Credits" onClose={() => setShowModal(false)}>
          <div className={styles["admin-billing-management__modal-form"]}>
            <p className={styles["admin-billing-management__modal-subtitle"]}>
              Add customer information and assign credits to their account.
            </p>

            <div className={styles["admin-billing-management__form-grid"]}>
              <Input
                type="select"
                label="Customer Name"
                required={true}
                placeholder="Select Customer Name"
                options={orgList}
                value={selectedOrgId}
                onChange={(e) => {
                  handleCustomerSelect(e.target.value);
                  setErrors({ ...errors, customer: "" });
                }}
                error={errors.customer}
              />

              <Input
                type="number"
                label="Credits"
                placeholder="Enter Credits"
                required={true}
                value={credits}
                onChange={(e) => {
                  setCredits(e.target.value);
                  setErrors({ ...errors, credits: "" });
                }}
                error={errors.credits}
              />
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                className="form-label"
                style={{ fontWeight: 600, fontSize: "14px" }}
              >
                Payment Status <span style={{ color: "#d54747" }}>*</span>
              </label>

              <div className={styles["admin-billing-management__radio-grid"]}>
                <label
                  className={`${styles["admin-billing-management__radio-card"]} ${
                    paymentStatus === "Pending"
                      ? styles["admin-billing-management__radio-card--active"]
                      : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentStatus"
                    value="Pending"
                    checked={paymentStatus === "Pending"}
                    onChange={(e) => {
                      setPaymentStatus(e.target.value);
                      setErrors({ ...errors, status: "" });
                    }}
                  />
                  <span>Pending</span>
                </label>

                <label
                  className={`${styles["admin-billing-management__radio-card"]} ${
                    paymentStatus === "Paid"
                      ? styles["admin-billing-management__radio-card--active"]
                      : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentStatus"
                    value="Paid"
                    checked={paymentStatus === "Paid"}
                    onChange={(e) => {
                      setPaymentStatus(e.target.value);
                      setErrors({ ...errors, status: "" });
                    }}
                  />
                  <span>Paid</span>
                </label>
              </div>
              {errors.status && (
                <span
                  style={{
                    fontSize: "13px",
                    color: "#d54747",
                    fontWeight: 600,
                  }}
                >
                  {errors.status}
                </span>
              )}
            </div>

            {paymentStatus === "Paid" && (
              <Input
                label="Transaction ID"
                placeholder="Enter Transaction ID"
                required={true}
                value={transactionId}
                onChange={(e) => {
                  setTransactionId(e.target.value);
                  setErrors({ ...errors, transactionId: "" });
                }}
                error={errors.transactionId}
              />
            )}

            <div className={styles["admin-billing-management__modal-actions"]}>
              <Button
                text={isSubmitting ? "Submitting..." : "Submit Details"}
                disabled={isSubmitting}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* ADD GIFT CREDITS MODAL (INTERNAL ONLY) */}
      {isInternalUser && showGiftModal && (
        <Modal
          title="Add Gift Credits"
          onClose={() => {
            setShowGiftModal(false);
            resetGiftForm();
          }}
        >
          <div className={styles["admin-billing-management__modal-form"]}>
            <p className={styles["admin-billing-management__modal-subtitle"]}>
              Gift complimentary credits to a customer's account at no charge.
            </p>

            <div className={styles["admin-billing-management__form-grid"]}>
              <Input
                type="select"
                label="Customer Name"
                required={true}
                placeholder="Select Customer Name"
                options={orgList}
                value={giftOrgId}
                onChange={(e) => {
                  setGiftOrgId(e.target.value);
                  setGiftErrors({ ...giftErrors, customer: "" });
                }}
                error={giftErrors.customer}
              />

              <Input
                type="number"
                label="Credits"
                placeholder="Enter Credits"
                required={true}
                value={giftCredits}
                onChange={(e) => {
                  setGiftCredits(e.target.value);
                  setGiftErrors({ ...giftErrors, credits: "" });
                }}
                error={giftErrors.credits}
              />
            </div>

            <Input
              type="textarea"
              label="Reason"
              placeholder="e.g. Onboarding goodwill"
              required={true}
              style={{ height: "100px" }}
              value={giftReason}
              onChange={(e) => {
                setGiftReason(e.target.value);
                setGiftErrors({ ...giftErrors, reason: "" });
              }}
              error={giftErrors.reason}
            />

            <div className={styles["admin-billing-management__modal-actions"]}>
              <Button
                text={isGiftSubmitting ? "Submitting..." : "Add Gift Credits"}
                icon={Gift}
                disabled={isGiftSubmitting}
                onClick={handleGiftSubmit}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* TRANSACTION ID MODAL */}
      {showTxnModal && (
        <Modal
          title="Enter Transaction ID"
          onClose={() => setShowTxnModal(false)}
        >
          <div className={styles["admin-billing-management__modal-form"]}>
            <Input
              label="Transaction ID"
              placeholder="Enter Full Transaction ID"
              value={txnIdInput}
              onChange={(e) => setTxnIdInput(e.target.value)}
              required
            />

            <div className={styles["admin-billing-management__modal-actions"]}>
              <Button
                text={isTxnSubmitting ? "Submitting..." : "Submit Details"}
                disabled={isTxnSubmitting}
                onClick={handleBillingUpdate}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* DELETE INVOICE MODAL */}
      {showDeleteModal && (
        <Modal
          title="Delete Invoice"
          onClose={() => {
            if (isDeleting) return;
            setShowDeleteModal(false);
            setDeleteTarget(null);
            setDeleteReason("");
          }}
        >
          <div className={styles["admin-billing-management__modal-form"]}>
            <p className={styles["admin-billing-management__modal-subtitle"]}>
              Are you sure you want to delete invoice{" "}
              <strong>{deleteTarget?.invoiceId}</strong>? This action cannot be
              undone.
            </p>

            <Input
              type="textarea"
              label="Reason (optional)"
              placeholder="e.g. Invoice delete"
              style={{ height: "100px" }}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
            />

            <div className={styles["admin-billing-management__modal-actions"]}>
              <Button
                text={isDeleting ? "Deleting..." : "Delete Invoice"}
                icon={Trash2}
                disabled={isDeleting}
                onClick={confirmDelete}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BillingManagement;
